import axios, { AxiosResponse } from "axios";
import { apiBaseUrl } from "@/constants/apiBaseUrl";
import { getAuth } from "firebase/auth";

export const getAuthToken = async (): Promise<string | null> => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("User token not found");
  return currentUser.getIdToken();
};

export const getHeaders = async (): Promise<HeadersInit> => {
  const token = await getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const axiosClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosClient.interceptors.request.use(
  async (config) => {
    try {
      const headers = await getHeaders();

      Object.entries(headers).forEach(([key, value]) => {
        config.headers.set(key, value);
      });
    } catch (error) {
      console.error("Error getting auth headers:", error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  <T>(response: AxiosResponse<T>): T => {
    return response.data;
  },
  (error) => {
    if (!error.response) {
      throw new Error("Network error. Please check your connection.");
    }

    const { status, data } = error.response;

    switch (status) {
      case 401:
        throw new Error("Authentication required. Please log in again.");

      case 403:
        throw new Error(
          "Access denied. You do not have permission to perform this action."
        );

      case 404:
        throw new Error("Resource not found.");

      case 429:
        if (error.config?.url?.includes("review-reminder")) {
          throw new Error(
            "An email was already sent in the last 5 minutes. Please wait before sending another."
          );
        }
        if (error.config?.url?.includes("review-reminder-sms")) {
          throw new Error(
            "An SMS was already sent in the last 1 minute. Please wait before sending another."
          );
        }
        throw new Error("Rate limit exceeded. Please try again later.");

      case 500:
        throw new Error("Server error. Please try again later.");

      default:
        const message =
          data?.error ||
          data?.message ||
          data?.details ||
          error.message ||
          `Request failed with status ${status}`;

        throw new Error(message);
    }
  }
);

const axiosPublicClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosPublicClient.interceptors.response.use(
  <T>(response: AxiosResponse<T>): T => {
    return response.data;
  },
  (error) => {
    if (!error.response) {
      throw new Error("Network error. Please check your connection.");
    }

    const { status, data } = error.response;

    switch (status) {
      case 403:
        if (data === "forbidden") {
          throw new Error("Please complete the verification check and try again.");
        }
        throw new Error(
          data?.message ||
            data?.error ||
            "Access denied. You do not have permission to perform this action.",
        );

      case 429:
        throw new Error("Too many requests. Please try again later.");

      case 500:
        throw new Error("Server error. Please try again later.");

      default:
        const message =
          data?.error ||
          data?.message ||
          error.message ||
          `Request failed with status ${status}`;

        throw new Error(message);
    }
  }
);

export { axiosClient, axiosPublicClient };
